/**
 * ProductsContext — Phase 5 update: tenant-scoped queries
 *
 * Core architecture (from Phase 1, unchanged):
 *   - Storefront (admin=false): one-time getDocs on mount. No live socket. Exposes
 *     refetch() so retailers can manually refresh the catalog.
 *   - Admin (admin=true): full live onSnapshot. Admin sees all product changes
 *     instantly for stock/price management.
 *   - Auth-gated: no Firestore read ever fires while unauthenticated.
 *   - Listener teardown on sign-out: immediate, independent of React unmount lifecycle.
 *   - filterDocs: strips soft-deleted products for storefront (defence-in-depth).
 *
 * Phase 5 additions:
 *
 *   wholesalerId — now read directly from useAuth() instead of a separate
 *     onAuthStateChanged + getIdTokenResult() listener (PROD-AUDIT FIX,
 *     see below).
 *
 *   Tenant-scoped queries
 *     Every getDocs / onSnapshot query now includes:
 *       where("wholesalerId", "==", wId)
 *     This is defence-in-depth — Firestore security rules (Phase 3) enforce
 *     the same constraint at the server, but client-side scoping ensures we
 *     never even attempt a cross-tenant read.
 *
 *   Null wholesalerId handling
 *     Platform admins (no wholesalerId) and unenrolled retailers both resolve
 *     to wholesalerId=null. In both cases products=[], loading=false, no error.
 *     - Platform admins use /platform-admin (Phase 7), not the product catalog.
 *     - Unenrolled retailers are redirected to /join by App.js WholesalerGate
 *       before they can reach any page that calls useProducts().
 *
 * PROD-AUDIT FIX (Critical — retailer workflow audit):
 *   The original implementation maintained its own onAuthStateChanged
 *   listener and read wholesalerId via an UN-forced getIdTokenResult().
 *   onAuthStateChanged only fires on real sign-in/sign-out transitions —
 *   it does NOT fire when WholesalerJoin.js calls AuthContext.refreshToken()
 *   after a retailer joins a wholesaler (that's a plain state update +
 *   client-side navigate(), not a Firebase auth-state transition). The
 *   result: right after completing the join flow, this context's internal
 *   wholesalerId stayed null, so the storefront's product query never ran
 *   — a brand-new retailer saw an empty catalog on /products until a full
 *   sign-out/sign-in (or up to an hour, whenever the ID token naturally
 *   refreshed). Sourcing wholesalerId directly from useAuth() — which
 *   AuthContext already keeps current via refreshToken()'s force:true
 *   getIdTokenResult() — fixes this at the source instead of duplicating
 *   (and getting out of sync with) that logic here.
 *
 * Security properties (all retained):
 *   - No Firestore read ever fires while unauthenticated.
 *   - Admin onSnapshot is torn down the moment auth is lost or wholesalerId changes.
 *   - Products state is wiped on sign-out.
 *   - filterDocs strips soft-deleted products for storefront.
 *   - Every query is scoped to the user's wholesalerId — zero cross-tenant data.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { db } from "../services/firebase";
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";

export const ProductsContext = createContext(null);

export function ProductsProvider({ children, admin = false }) {
  const { user, wholesalerId, loading: authLoading } = useAuth();

  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Holds the active onSnapshot unsubscribe fn so we can tear it down
  // immediately when auth state or wholesalerId changes, independent of
  // React unmount lifecycle.
  const snapshotUnsubRef = useRef(null);

  // M-10 fix: filter helper — storefront never shows soft-deleted products.
  // Admin sees everything (including deleted) so they can audit/undo.
  const filterDocs = useCallback((docs, forAdmin) =>
    docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => forAdmin || (!p.deleted && p.status !== "draft" && p.isPublished !== false)),
  []);

  // Tears down the Firestore listener and wipes local state.
  // Called both on explicit sign-out and on unmount.
  const teardown = useCallback(() => {
    if (snapshotUnsubRef.current) {
      snapshotUnsubRef.current();
      snapshotUnsubRef.current = null;
    }
    setProducts([]);
    setLoading(false);
    setError(null);
    setLastFetched(null);
  }, []);

  // Phase 5: tenant-scoped one-time fetch used by storefront path.
  // Exposed via context as refetch() so components can trigger a manual
  // catalog refresh (e.g. pull-to-refresh). Safe to call multiple times.
  // PROD-AUDIT FIX: wholesalerId now comes from the closure (sourced from
  // useAuth()) instead of a ref populated by a separate, can-go-stale
  // listener — so refetch() always sees the current tenant.
  const fetchProducts = useCallback(async () => {
    const wId = wholesalerId;

    if (!wId) {
      // No wholesalerId: platform admin or unenrolled retailer.
      // App.js WholesalerGate prevents unenrolled users from reaching any
      // page that calls useProducts(). Platform admins have their own panel.
      // Return empty state cleanly — no Firestore call, no error displayed.
      setProducts([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, "products"),
          where("wholesalerId", "==", wId),   // Phase 5: tenant isolation
          orderBy("name")
        )
      );
      setProducts(filterDocs(snap.docs, false)); // storefront: strip deleted
      setLastFetched(Date.now());
    } catch (err) {
      console.error("ProductsContext fetch error:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [wholesalerId, filterDocs]);

  useEffect(() => {
    // Wait for AuthContext to resolve the token before deciding anything —
    // avoids a flash of "no products" while wholesalerId is still in-flight.
    if (authLoading) return;

    if (!user) {
      teardown();
      return;
    }

    if (admin) {
      // Admin path: persistent live onSnapshot, rebuilt whenever wholesalerId
      // changes (tears down the previous tenant's listener first so two
      // tenants' data can never be mixed in `products`).
      if (snapshotUnsubRef.current) {
        snapshotUnsubRef.current();
        snapshotUnsubRef.current = null;
      }

      if (!wholesalerId) {
        // Platform admin or unenrolled — no products to show in admin panel.
        setProducts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const unsub = onSnapshot(
        query(
          collection(db, "products"),
          where("wholesalerId", "==", wholesalerId),  // Phase 5: tenant isolation
          orderBy("name")
        ),
        (snap) => {
          setProducts(filterDocs(snap.docs, true)); // admin sees soft-deleted too
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("ProductsContext snapshot error:", err);
          setError("Failed to load products. Please try again.");
          setLoading(false);
        }
      );
      snapshotUnsubRef.current = unsub;
    } else {
      // Storefront path: one-time fetch, no persistent listener. Re-runs
      // whenever wholesalerId changes (e.g. right after a retailer joins).
      fetchProducts();
    }

    // Cleanup: tear down any active Firestore listener on unmount or before
    // this effect re-runs (e.g. wholesalerId changed).
    return () => {
      if (snapshotUnsubRef.current) {
        snapshotUnsubRef.current();
        snapshotUnsubRef.current = null;
      }
    };
  }, [admin, user, wholesalerId, authLoading, fetchProducts, filterDocs, teardown]);

  return (
    <ProductsContext.Provider
      value={{ products, loading, error, lastFetched, refetch: fetchProducts }}
    >
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used within ProductsProvider (check index.js)");
  return ctx;
}